import React, { useMemo, useState } from 'react';
import { Table, TableRow, TableCell, TableBody, Button, Card, CardHeader, Typography, Hidden, makeStyles } from '@material-ui/core';

import classNames from 'classnames';
import { printAssetCategory } from '../../../../entities/AssetCategory';
import { LibraryItemType, printLibraryItemType } from '../../../../entities/LibraryItemType';
import { PremiumSermon } from '../../../../entities/PremiumSermon';
import { PremiumSermonAsset } from '../../../../entities/PremiumSermonAsset';

import { LibraryItemIcon } from './LibraryItemIcon';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { orderBy } from '../../../../utilities/orderBy';

const useStyles = makeStyles((theme) => ({
    grayedOut: {
        opacity: 0.5
    },
    tableHover: {
        '&:hover': {
            backgroundColor: 'rgb(232, 240, 254)'
        }
    },
    tableText: {
        color: "#5f6368",
        fontSize: "13px",
        fontWeight: 500,
        fontFamily: "Roboto, Helvetica",
    },
    iconText: {
        verticalAlign: "super"
    },
    crumbs: {
        color: "#5f6368",
        fontSize: "18px",
        fontWeight: 500,
        fontFamily: "Roboto, Helvetica",
        verticalAlign: "middle",
    },
    crumbButton: {
        borderRadius: "5px",
        textTransform: "none",
        paddingLeft: "7px",
        paddingRight: "0px"
    },
    crumbIcon: {
        verticalAlign: "middle",
        color: "#5f6368"
    },
    mobileMantleViewTitle: {
        color: "#5f6368",
        fontSize: "18px",
        fontWeight: 500,
        fontFamily: "Roboto, Helvetica",
        verticalAlign: "middle",
        paddingTop: '16px',
        display: 'block',
        minHeight: "32px",
        paddingLeft: 0
    },
}));

interface premiumSermonTableProps {
    currentSermon: PremiumSermon;
    openPreview: (asset:PremiumSermonAsset) => void;
}

const getBreadCrumbPath = (items:PremiumSermonAsset[], activeFolderId:string | null) => {
    if(!activeFolderId)
        return [];
        
    const assets:PremiumSermonAsset[] = [];
    let folderId: string | undefined = activeFolderId;
    while(folderId !== undefined){
        const folder = items.find(i => i.id === folderId);
        if(folder){
            assets.push(folder);
            folderId = folder.parentId;
            return;
        } else {
            folderId = undefined;
            return;
        }
    }
    return assets.reverse();
}

export function PremiumSermonTable(props:premiumSermonTableProps){
    const { currentSermon } = props;
    const [activefolderId, setActiveFolderId] = useState<string | null>(null);

    const seriesArray = useMemo(() => 
        currentSermon.assets.filter(a => a.type === LibraryItemType.SermonSeries).sort(orderBy.string(a => a.name, "Ascending"))
    ,[currentSermon]);
    const folderArray = useMemo(() => 
        currentSermon.assets.filter(a => a.type === LibraryItemType.Folder).sort(orderBy.string(a => a.name, "Ascending"))
    , [currentSermon]);
    const assetArray = useMemo(() => 
        currentSermon.assets.filter(a => a.type !== LibraryItemType.Folder && a.type !== LibraryItemType.SermonSeries).sort(orderBy.string(a => a.name, "Ascending"))
    ,[currentSermon]);

    const assetHistory = useMemo(() => getBreadCrumbPath(currentSermon.assets, activefolderId) ?? [], [activefolderId, currentSermon])

    const mobileGoBackBreadCrumb = () => {
        const history = [...assetHistory];
        history.splice(assetHistory.length - 1, 1);

        const newId = assetHistory.length > 0 ?
            assetHistory.length === 1 ? null :
            assetHistory[assetHistory.length - 2].id :
            null;

        setActiveFolderId(newId)
    }

    return (
        <>
            <Hidden smDown>
                <DesktopTable
                    folderArray={folderArray} 
                    seriesArray={seriesArray} 
                    assetArray={assetArray} 
                    activefolderId={activefolderId}
                    openAssetContainer={(assetId) => setActiveFolderId(assetId)}
                    openPreview={(asset) => props.openPreview(asset)}
                    sermonName={props.currentSermon.name}
                    assetHistory={assetHistory}
                    breadCrumbClick={(assetId) => setActiveFolderId(assetId)}
                    clearActiveFolderId={() => setActiveFolderId(null)}
                    />
            </Hidden>
            <Hidden mdUp>
                <MobileTable
                    folderArray={folderArray} 
                    seriesArray={seriesArray} 
                    assetArray={assetArray} 
                    activefolderId={activefolderId}
                    openAssetContainer={(assetId) => setActiveFolderId(assetId)}
                    openPreview={(asset) => props.openPreview(asset)}
                    assetHistory={assetHistory}
                    breadCrumbClick={mobileGoBackBreadCrumb}
                    />
            </Hidden>
        </>
    )
}


interface DesktopProps {
    openPreview: (asset:PremiumSermonAsset) => void;
    openAssetContainer: (assetId:string) => void;
    breadCrumbClick: (assetId:string) => void;
    folderArray: PremiumSermonAsset[];
    seriesArray: PremiumSermonAsset[];
    assetArray: PremiumSermonAsset[];
    activefolderId: string | null;
    sermonName: string;
    assetHistory: PremiumSermonAsset[];
    clearActiveFolderId: () => void;
}

function DesktopTable(props: DesktopProps) {
    const {assetArray, folderArray, seriesArray, activefolderId, sermonName, assetHistory} = props;
    const classes = useStyles();

    const renderRow = (row: PremiumSermonAsset, hasOnClick:boolean) => {
        if (row.parentId !== activefolderId) {
            return;
        }

        const onClickFunction = (row.type === LibraryItemType.Folder || row.type === LibraryItemType.SermonSeries)
            ? () => props.openAssetContainer(row.id)
            : () => props.openPreview(row)

        return (
            <React.Fragment>
                <TableRow
                    key={row.id}
                    onClick={hasOnClick ? onClickFunction : undefined}
                    className={!hasOnClick ? classes.grayedOut : classes.tableHover}
                >
                    <TableCell component="th" scope="row">
                        <span>
                            <LibraryItemIcon itemType={row.type} isTemplate={false} />
                            <span
                                className={classNames(classes.tableText, classes.iconText)}
                            >
                                {row.name}
                            </span>
                        </span>
                    </TableCell>
                    <TableCell>
                        <span className={classNames(classes.tableText)}>
                            {row.category ? printAssetCategory(row.category) : printLibraryItemType(row.type)}
                        </span>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    }

    return(
        <>
        <div style={{ marginTop: 8 }}>
            <Button
                size="small"
                className={classNames(classes.crumbs, classes.crumbButton)}
                onClick={props.clearActiveFolderId}
            >
                {sermonName}
            </Button>
            {assetHistory.map((item: PremiumSermonAsset, index: number) => {
                return (
                    <span key={index}>
                        <ChevronRightIcon className={classes.crumbIcon} />
                        <Button
                            size="small"
                            className={classNames(classes.crumbs, classes.crumbButton)}
                            onClick={() => props.breadCrumbClick(item.id)}
                        >
                            {item.name}
                        </Button>
                    </span>
                );
            })}
        </div>
        <Table>
            <TableBody>
                {folderArray.map((row: PremiumSermonAsset) => {
                    return (
                        renderRow(row, true)
                    );
                })}
                {seriesArray.map((row: PremiumSermonAsset) => {
                    return (
                        renderRow(row, true)
                    );
                })}
                {assetArray.map((row: PremiumSermonAsset) => {
                        return (
                            renderRow(row, row.type === LibraryItemType.BuilderAsset)
                        );
                    })}
            </TableBody>
        </Table>
        </>
    )
}

interface MobileTableProps {
    openPreview: (asset:PremiumSermonAsset) => void;
    openAssetContainer: (assetId:string) => void;
    breadCrumbClick: () => void;
    folderArray: PremiumSermonAsset[];
    seriesArray: PremiumSermonAsset[];
    assetArray: PremiumSermonAsset[];
    activefolderId: string | null;
    assetHistory: PremiumSermonAsset[];
}

function MobileTable(props:MobileTableProps){
    const {assetArray, folderArray, seriesArray, activefolderId, assetHistory} = props;
    const classes = useStyles();
    
    const renderCard = (row:PremiumSermonAsset, hasOnClick:boolean) => {
        if (row.parentId !== activefolderId) {
            return;
        }

        const onClickFunction = (row.type === LibraryItemType.Folder || row.type === LibraryItemType.SermonSeries)
        ? () => props.openAssetContainer(row.id)
        : () => props.openPreview(row)

        return(
            <Card square={true} key={row.id} onClick={hasOnClick ? onClickFunction : undefined}>
                <CardHeader
                        avatar={<LibraryItemIcon itemType={row.type} isTemplate={false} />}
                        title={
                        <Typography variant="body2" >
                            {row.name}
                        </Typography>
                    }
                    disableTypography
                    subheader={
                        <>
                            <Typography variant="body1" >
                            {row.category ? printAssetCategory(row.category) : printLibraryItemType(row.type)}
                            </Typography>
                        </>
                    }
                />
            </Card>
        )
    }

    return (
        <>
            {assetHistory.length > 0 && 
                <Typography 
                    variant="subtitle1" 
                    gutterBottom 
                    className={classes.mobileMantleViewTitle}
                    onClick={() => props.breadCrumbClick()}
                >
                    <ChevronLeftIcon className={classes.crumbIcon} /> Back
                </Typography>
            }
            {folderArray.length > 0 && activefolderId === null &&
                <Typography variant="subtitle1" >
                    Folders
                </Typography>
            }
            {folderArray.map((row: PremiumSermonAsset) => {
                return (
                    renderCard(row, true)
                );
            })}
            {seriesArray.length > 0 && activefolderId === null &&
                <Typography variant="subtitle1" >
                    Series
                </Typography>
            }
            {seriesArray.map((row: PremiumSermonAsset) => {
                return (
                    renderCard(row, true)
                );
            })}               
            {assetArray.length > 0 &&
                <Typography variant="subtitle1" >
                    Assets
                </Typography>
            }
            {assetArray.map((row: PremiumSermonAsset) => {
                return (
                    renderCard(row, row.type === LibraryItemType.BuilderAsset)
                );
            })}
        </>
    );
}